import { type ManagerHubVisibilityParams } from './types'
import {
  isManagerHubPromotionSignalActive,
  parseManagerHubPromotionSignalPropertyValue,
  shouldShowManagerHub,
} from './utils'
import { SESSION_STORAGE_KEY } from '../common/constants/constants'

/**
 * A pending Manager Hub visit is a Hub visit made while logged out. It cannot write the
 * `promotionSignal.managerHub` user property at the time, so it is kept in session storage (tab-scoped,
 * so short-lived) and recorded as that signal once the visitor is authenticated.
 *
 * Web only by construction: the `window` guard is the shared-library equivalent of the apps' web flag.
 * Storage access is best-effort because `sessionStorage` throws when the store is full or blocked by
 * browser policy (e.g. Safari private mode); a failure must never break the public Hub. Callers own
 * logging, since this library has no Sentry binding.
 */

/** Stores the pending visit. Returns false when session storage rejected the write. */
export const setPendingManagerHubVisit = (now: number = Date.now()): boolean => {
  if (typeof window === 'undefined') return false
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY.MANAGER_HUB_PENDING_VISIT, String(now))
    return true
  } catch {
    return false
  }
}

/** The timestamp of the pending Manager Hub visit, or `undefined` when none is stored. */
export const getPendingManagerHubVisit = (): number | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    const storedValue = window.sessionStorage.getItem(SESSION_STORAGE_KEY.MANAGER_HUB_PENDING_VISIT)
    if (storedValue == null) return undefined
    const timestamp = parseManagerHubPromotionSignalPropertyValue(storedValue)
    if (timestamp === undefined) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY.MANAGER_HUB_PENDING_VISIT)
    }
    return timestamp
  } catch {
    return undefined
  }
}

export const clearPendingManagerHubVisit = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY.MANAGER_HUB_PENDING_VISIT)
  } catch {
    // Blocked storage throws on removal too; nothing was stored in that case.
  }
}

export enum PendingManagerHubVisitAction {
  /** Member with Hub access and no active window: record the visit as the promotion signal, then clear it on success. */
  WRITE = 'WRITE',
  /** A promotion window is already active: an organic visit must not refresh it, so drop the pending visit. */
  DISCARD = 'DISCARD',
  /** No Hub access with the inputs known so far (flags and customer context may still be loading): keep the visit and wait. */
  WAIT = 'WAIT',
}

/**
 * Decides what an authenticated session does with a pending Manager Hub visit. Mirrors the authenticated
 * organic-visit rule: only a member with Hub access with no active promotion window starts one.
 */
export const resolvePendingManagerHubVisit = ({
  managerHubPromotionSignal,
  now = Date.now(),
  ...accessParams
}: ManagerHubVisibilityParams & {
  managerHubPromotionSignal: unknown
  now?: number
}): PendingManagerHubVisitAction => {
  if (isManagerHubPromotionSignalActive(managerHubPromotionSignal, now)) {
    return PendingManagerHubVisitAction.DISCARD
  }
  return shouldShowManagerHub(accessParams) ? PendingManagerHubVisitAction.WRITE : PendingManagerHubVisitAction.WAIT
}
